import { useMemo } from 'react';
import { Card, CardBody, Tooltip } from '@repo/ui';
import { Icon } from '@repo/ui';
import { GroupByOption } from '@repo/types';
import { numberUtil } from '@xtnd/web/shared/utils';
import {
  DataTable,
  DataTablePaginatedResponse,
  type DataTableColumn,
} from '@xtnd/web/shared/components/DataTable';

import {
  ArCollectionQueueFilter,
  ArCollectionQueueParams,
  GROUP_BY,
  CollectionItem,
} from '../../types';
import { useTableParams } from '../../hooks';
import { useCollectionQueue, useGetMatters } from '../../queries';
import MattersTable from './MattersTable';
import ActionToolbar from './ActionToolbar';
import { useMatterARContext } from '../common';
import { DAYS_PAST_DUE } from '../../constants';

const INITIAL_FILTERS: ArCollectionQueueFilter = {
  search: '',
  groupBy: GroupByOption.CLIENT,
  daysPastDue: 30,
};

export default function CollectionsQueue() {
  const { tableParams: queryParams, setParams: setQueryParams } = useTableParams({
    page: 1,
    limit: 50,
    sorts: { daysPastDue: 'desc' },
    filters: INITIAL_FILTERS,
  });

  const filters = (queryParams.filters as ArCollectionQueueFilter) ?? INITIAL_FILTERS;

  const { data, isLoading, error, refetch } = useCollectionQueue(
    queryParams as ArCollectionQueueParams,
    { enabled: true }
  );

  const { data: resMatters, isFetching: isFetchingMatters } = useGetMatters(
    queryParams as ArCollectionQueueParams
  );

  const { openMatterDrawer } = useMatterARContext();

  const isDisplayArMattersTable = useMemo(() => {
    return !!filters.entityId;
  }, [filters]);

  const handleRowClicked = (row: CollectionItem) => {
    if (filters && filters.groupBy === GroupByOption.NONE && row.matterId && row.clientId) {
      openMatterDrawer(row.matterId, row.clientId, filters.daysPastDue);
      return;
    }

    const newFilters = { ...filters };
    if (filters.groupBy === GroupByOption.CLIENT && row.clientId) {
      newFilters.entityId = row.clientId;
    } else if (filters.groupBy === GroupByOption.ORIGINATING && row.originatingAttorneyId) {
      newFilters.entityId = row.originatingAttorneyId;
    } else if (filters.groupBy === GroupByOption.RESPONSIBLE && row.responsibleAttorneyId) {
      newFilters.entityId = row.responsibleAttorneyId;
    }

    setQueryParams({ ...queryParams, filters: newFilters, page: 1 });
  };

  const handleChangeFilters = (nextFilters: ArCollectionQueueFilter) => {
    const nextGroupBy = nextFilters.groupBy;
    if (nextGroupBy !== filters.groupBy) {
      setQueryParams({
        ...queryParams,
        page: 1,
        filters: nextFilters,
      });
      return;
    }

    setQueryParams({ ...queryParams, filters: nextFilters, page: 1 });
  };

  return (
    <Card className="pt-0 mt-0">
      <CardBody className="p-0 relative">
        {!isDisplayArMattersTable && (
          <DataTable
            columns={tableColumns[filters.groupBy] ?? []}
            data={
              {
                data: data?.data,
                total: data?.pagination.total,
              } as DataTablePaginatedResponse<CollectionItem>
            }
            error={error}
            getRowKey={(_, index: number) => `row-${index}`}
            onRetry={() => refetch()}
            isLoading={isLoading}
            pagination={data?.pagination}
            tableContainerClassName="overflow-y-auto relative"
            tableClassName="min-w-full divide-y divide-gray-200 relative"
            pageSizeOptions={[20, 50, 100, 200]}
            emptyState={{
              icon: 'HiClipboardList',
              title: 'No outstanding invoices available',
              description: 'There are no outstanding invoices found.',
            }}
            onPaginationChange={pagination =>
              setQueryParams({ filters: { ...filters }, ...pagination })
            }
            onSort={sortBy => setQueryParams({ ...queryParams, sorts: { ...sortBy } })}
            sorting={queryParams.sorts}
            onRowClick={handleRowClicked}
            renderHeader={() => (
              <ActionToolbar
                isDataTableLoading={isLoading}
                filters={filters}
                onChange={handleChangeFilters}
                queryParams={{ ...queryParams, limit: data?.pagination?.total }}
              />
            )}
            enableColumnResizing
            columnWidthsStorageKey={`collection-table-${filters.groupBy}`}
          />
        )}
        {isDisplayArMattersTable && (
          <MattersTable
            isLoading={isFetchingMatters}
            matters={resMatters?.data || []}
            onBackToClient={() =>
              setQueryParams({ ...queryParams, filters: { ...filters, entityId: '' } })
            }
          />
        )}
      </CardBody>
    </Card>
  );
}

const tableColumns: Record<GROUP_BY, DataTableColumn<CollectionItem>[]> = {
  [GroupByOption.NONE]: [
    {
      key: 'clientName',
      header: 'Client',
      align: 'left',
      isResizable: true,
      defaultWidth: 200,
      renderCell: row => (
        <Tooltip content={row.clientName ?? ''} position="auto" className="rounded-md w-fit">
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className="text-sm overflow-hidden text-ellipsis">{row.clientName}</div>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'matterDisplayNumber',
      header: 'Matter',
      align: 'left',
      isResizable: true,
      defaultWidth: 200,
      renderCell: row => (
        <Tooltip
          content={row.matterDisplayNumber ?? ''}
          position="auto"
          className="rounded-md w-fit"
        >
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className="text-sm overflow-hidden text-ellipsis">{row.matterDisplayNumber}</div>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'originating',
      header: 'Originating Attorney',
      align: 'left',
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <Tooltip
          content={row.originatingAttorneyName ?? ''}
          position="auto"
          className="rounded-md w-fit"
        >
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className="text-sm overflow-hidden text-ellipsis">
              {row.originatingAttorneyName}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'responsible',
      header: 'Responsible Attorney',
      align: 'left',
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <Tooltip
          content={row.responsibleAttorneyName ?? ''}
          position="auto"
          className="rounded-md w-fit"
        >
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className="text-sm overflow-hidden text-ellipsis">
              {row.responsibleAttorneyName}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'outstandingFees',
      header: 'Outstanding Fees',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.outstandingFees || 0)}</div>
        </div>
      ),
    },
    {
      key: 'outstandingExpenses',
      header: 'Outstanding Expenses',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.outstandingExpenses || 0)}</div>
        </div>
      ),
    },
    {
      key: 'totalOutstanding',
      header: 'Total AR',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.totalOutstanding)}</div>
        </div>
      ),
    },
    {
      key: 'daysPastDue',
      header: 'DPD',
      align: 'right',
      isResizable: true,
      isSortable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className={`text-sm ${row.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}>
            {row.maxDaysPastDue}
          </div>
        </div>
      ),
    },
    {
      key: 'outstanding',
      header: 'O/S Invoices',
      align: 'right',
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{row.numOfInvoices}</div>
        </div>
      ),
    },
    {
      key: '',
      header: '',
      align: 'center',
      renderCell: () => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <button>
            <Icon name="FaAngleRight" size="sm" className="opacity-100" />
          </button>
        </div>
      ),
    },
  ],
  [GroupByOption.CLIENT]: [
    {
      key: 'clientName',
      header: 'Client',
      align: 'left',
      isResizable: true,
      defaultWidth: 300,
      renderCell: row => (
        <Tooltip content={row.clientName ?? ''} position="auto" className="rounded-md">
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light ">
            <div className="text-sm overflow-hidden text-ellipsis">{row.clientName}</div>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'outstandingFees',
      header: 'Outstanding Fees',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.outstandingFees || 0)}</div>
        </div>
      ),
    },
    {
      key: 'outstandingExpenses',
      header: 'Outstanding Expenses',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.outstandingExpenses || 0)}</div>
        </div>
      ),
    },
    {
      key: 'totalOutstanding',
      header: 'Total AR',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.totalOutstanding)}</div>
        </div>
      ),
    },
    {
      key: 'daysPastDue',
      header: 'DPD',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 100,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className={`text-sm ${row.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}>
            {row.maxDaysPastDue}
          </div>
        </div>
      ),
    },
    {
      key: 'numOfMatters',
      header: 'Matters',
      align: 'right',
      isResizable: true,
      defaultWidth: 100,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{row.numOfMatters}</div>
        </div>
      ),
    },
    {
      key: 'outstanding',
      header: 'O/S Invoices',
      align: 'right',
      isResizable: true,
      defaultWidth: 110,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{row.numOfInvoices}</div>
        </div>
      ),
    },
    {
      key: '',
      header: '',
      align: 'center',
      renderCell: () => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <button>
            <Icon name="FaAngleRight" size="sm" className="opacity-100" />
          </button>
        </div>
      ),
    },
  ],
  [GroupByOption.ORIGINATING]: [
    {
      key: 'originatingAttorneyName',
      header: 'Originating Attorney',
      align: 'left',
      isResizable: true,
      defaultWidth: 250,
      renderCell: row => (
        <Tooltip
          content={row.originatingAttorneyName ?? ''}
          position="auto"
          className="rounded-md w-fit"
        >
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className="text-sm overflow-hidden text-ellipsis">
              {row.originatingAttorneyName}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'outstandingFees',
      header: 'Outstanding Fees',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.outstandingFees || 0)}</div>
        </div>
      ),
    },
    {
      key: 'outstandingExpenses',
      header: 'Outstanding Expenses',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.outstandingExpenses || 0)}</div>
        </div>
      ),
    },
    {
      key: 'totalOutstanding',
      header: 'Total AR',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.totalOutstanding)}</div>
        </div>
      ),
    },
    {
      key: 'daysPastDue',
      header: 'DPD',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 100,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className={`text-sm ${row.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}>
            {row.maxDaysPastDue}
          </div>
        </div>
      ),
    },
    {
      key: 'numOfMatters',
      header: 'Matters',
      align: 'right',
      isResizable: true,
      defaultWidth: 110,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{row.numOfMatters}</div>
        </div>
      ),
    },
    {
      key: 'outstanding',
      header: 'O/S Invoices',
      align: 'right',
      isResizable: true,
      defaultWidth: 110,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{row.numOfInvoices}</div>
        </div>
      ),
    },
    {
      key: '',
      header: '',
      align: 'center',
      renderCell: () => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <button>
            <Icon name="FaAngleRight" size="sm" className="opacity-100" />
          </button>
        </div>
      ),
    },
  ],
  [GroupByOption.RESPONSIBLE]: [
    {
      key: 'responsibleAttorneyName',
      header: 'Responsible Attorney',
      align: 'left',
      isResizable: true,
      defaultWidth: 250,
      renderCell: row => (
        <Tooltip
          content={row.responsibleAttorneyName ?? ''}
          position="auto"
          className="rounded-md w-fit"
        >
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light ">
            <div className="text-sm overflow-hidden text-ellipsis">
              {row.responsibleAttorneyName}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'outstandingFees',
      header: 'Outstanding Fees',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.outstandingFees || 0)}</div>
        </div>
      ),
    },
    {
      key: 'outstandingExpenses',
      header: 'Outstanding Expenses',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.outstandingExpenses || 0)}</div>
        </div>
      ),
    },
    {
      key: 'totalOutstanding',
      header: 'Total AR',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{numberUtil.formatCurrency(row.totalOutstanding)}</div>
        </div>
      ),
    },
    {
      key: 'daysPastDue',
      header: 'DPD',
      align: 'right',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className={`text-sm ${row.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}>
            {row.maxDaysPastDue}
          </div>
        </div>
      ),
    },
    {
      key: 'numOfMatters',
      header: 'Matters',
      align: 'right',
      defaultWidth: 150,
      isResizable: true,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{row.numOfMatters}</div>
        </div>
      ),
    },
    {
      key: 'outstanding',
      header: 'O/S Invoices',
      align: 'right',
      isResizable: true,
      defaultWidth: 150,
      renderCell: row => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <div className="text-sm">{row.numOfInvoices}</div>
        </div>
      ),
    },
    {
      key: '',
      header: '',
      align: 'center',
      renderCell: () => (
        <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          <button>
            <Icon name="FaAngleRight" size="sm" className="opacity-100" />
          </button>
        </div>
      ),
    },
  ],
};
